import React, { useMemo } from "react";

import { MenuItem, SelectChangeEvent } from "@mui/material";
import { Select } from "Components/Form";
import { zonedTimeToUtc } from "date-fns-tz";
import { DespatchTask } from "Models/despatch.task";
import { Task, TaskStartDatePeriod } from "Models/task";
import { useTranslation } from "react-i18next";
import { UseState } from "Types";
import {
  AFTERNOON_END,
  AFTERNOON_START,
  EVENING_END,
  EVENING_START,
  getStartDatePeriods,
  getStartDateRangeLabel,
  MORNING_END,
  MORNING_START,
  TASK_DATE_MINUTES,
} from "Util/startDate";
import { useAppState } from "Context/StateContext";

interface ChangeTaskTimeDefaultPeriodFormProps {
  task: DespatchTask | Task;
  setTask: UseState<DespatchTask | Task | undefined>;
  isLoading: boolean;

  setWhatHasChanged?: (field: string) => void;
}
export const ChangeTaskTimeSelectPeriod = ({
  task,
  isLoading,
  setTask,
  setWhatHasChanged,
}: ChangeTaskTimeDefaultPeriodFormProps) => {
  const { t: translate } = useTranslation();
  const defaultTimePeriods = useMemo(() => getStartDatePeriods(new Date()), []);
  const { currentUser, userTimezone } = useAppState();
  const preferences = currentUser?.preferences;

  const getDateRangeValue = (value?: number) => {
    switch (value) {
      case TaskStartDatePeriod.MORNING:
        return getStartDateRangeLabel(defaultTimePeriods.morning, { preferences });
      case TaskStartDatePeriod.AFTERNOON:
        return getStartDateRangeLabel(defaultTimePeriods.afternoon, { preferences });
      case TaskStartDatePeriod.EVENING:
        return getStartDateRangeLabel(defaultTimePeriods.evening, {
          considerEvening: true,
          preferences,
        });
      case TaskStartDatePeriod.FULL_DAY:
        return getStartDateRangeLabel(defaultTimePeriods.fullDay, { isFullDay: true, preferences });
      case TaskStartDatePeriod.CUSTOM:
        return translate("custom");
      default:
        return "";
    }
  };

  const handleChangePeriod = (periodValue: TaskStartDatePeriod) => {
    const startTime = task?.startDate ? new Date(task.startDate) : new Date();
    const endTime = task?.endDate ? new Date(task.endDate) : new Date();

    switch (periodValue) {
      case TaskStartDatePeriod.MORNING:
        startTime.setHours(MORNING_START);
        startTime.setMinutes(TASK_DATE_MINUTES);

        endTime.setHours(MORNING_END);
        endTime.setMinutes(TASK_DATE_MINUTES);
        break;
      case TaskStartDatePeriod.AFTERNOON:
        startTime.setHours(AFTERNOON_START);
        startTime.setMinutes(TASK_DATE_MINUTES);

        endTime.setHours(AFTERNOON_END);
        endTime.setMinutes(TASK_DATE_MINUTES);
        break;
      case TaskStartDatePeriod.EVENING:
        startTime.setHours(EVENING_START);
        startTime.setMinutes(TASK_DATE_MINUTES);

        endTime.setHours(EVENING_END);
        endTime.setMinutes(59);
        break;
      case TaskStartDatePeriod.FULL_DAY:
        startTime.setHours(MORNING_START);
        startTime.setMinutes(TASK_DATE_MINUTES);

        endTime.setHours(AFTERNOON_END);
        endTime.setMinutes(TASK_DATE_MINUTES);
        break;
      default:
        setTask((state) =>
          state
            ? {
                ...state,
                period: periodValue,
              }
            : ({ startDate } as DespatchTask)
        );

        return;
    }

    const startDate = zonedTimeToUtc(startTime, userTimezone.name).toISOString();

    const endDate = zonedTimeToUtc(endTime, userTimezone.name).toISOString();

    setTask((state) =>
      state
        ? {
            ...state,
            startDate,
            endDate,
            period: periodValue,
          }
        : ({ startDate } as DespatchTask)
    );

    setWhatHasChanged?.("period");
  };

  return (
    <Select
      label={translate("period")}
      value={task?.period || ""}
      onChange={(event: SelectChangeEvent<any>) => handleChangePeriod(event.target.value)}
      renderValue={() => getDateRangeValue(task?.period)}
      disabled={isLoading}
    >
      <MenuItem value={TaskStartDatePeriod.MORNING}>
        {`${translate("morning")} (${getStartDateRangeLabel(defaultTimePeriods.morning, { preferences })})`}
      </MenuItem>
      <MenuItem value={TaskStartDatePeriod.AFTERNOON}>
        {`${translate("afternoon")} (${getStartDateRangeLabel(defaultTimePeriods.afternoon, { preferences })})`}
      </MenuItem>
      <MenuItem value={TaskStartDatePeriod.EVENING}>
        {`${translate("evening")} (${getStartDateRangeLabel(defaultTimePeriods.evening, {
          considerEvening: true,
          preferences,
        })})`}
      </MenuItem>
      <MenuItem value={TaskStartDatePeriod.FULL_DAY}>
        {`${translate("fullDay")} (${getStartDateRangeLabel(defaultTimePeriods.fullDay, {
          isFullDay: true,
          preferences,
        })})`}
      </MenuItem>
      <MenuItem value={TaskStartDatePeriod.CUSTOM}>{translate("custom")}</MenuItem>
    </Select>
  );
};
